<template>
  <div class="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/70 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-slate-50 dark:bg-slate-800/50">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.session', 'Sesión') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.opened', 'Abierta') }}
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.closed', 'Cerrada') }}
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.transactions', 'Transacciones') }}
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.totalSales', 'Ventas') }}
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.totalExpenses', 'Gastos') }}
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.netCashFlow', 'Flujo neto') }}
            </th>
            <th
              class="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-slate-300"
            >
              {{ tr('app.views.cashRegister.actions', 'Acciones') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
          <tr
            v-for="(report, index) in reports"
            :key="index"
            class="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
          >
            <td class="px-4 py-3">
              <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                #{{ report.session_number }}
              </div>
            </td>
            <td class="px-4 py-3">
              <div class="text-sm text-slate-600 dark:text-slate-400">
                {{ formatDateTime(report.opened_at) }}
              </div>
            </td>
            <td class="px-4 py-3">
              <div class="text-sm text-slate-600 dark:text-slate-400">
                {{ report.closed_at ? formatDateTime(report.closed_at) : '-' }}
              </div>
            </td>
            <td class="px-4 py-3 text-right">
              <div class="text-sm text-slate-900 dark:text-slate-100">
                {{ report.total_transactions }}
              </div>
            </td>
            <td class="px-4 py-3 text-right">
              <div class="text-sm font-medium text-green-600 dark:text-green-400">
                {{ formatCurrency(report.total_sales || 0) }}
              </div>
            </td>
            <td class="px-4 py-3 text-right">
              <div class="text-sm font-medium text-amber-600 dark:text-amber-400">
                {{ formatCurrency(report.total_expenses || 0) }}
              </div>
            </td>
            <td class="px-4 py-3 text-right">
              <div
                class="text-sm font-medium"
                :class="(report.net_cash_flow || 0) >= 0 ? 'text-sky-700 dark:text-sky-300' : 'text-red-600 dark:text-red-400'"
              >
                {{ formatCurrency(report.net_cash_flow || 0) }}
              </div>
            </td>
            <td class="px-4 py-3 text-center">
              <BaseButton
                variant="secondary"
                size="sm"
                outlined
                class="px-3"
                @click="$emit('view-details', report.session_id)"
              >
                <template #icon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path
                      fill-rule="evenodd"
                      d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </template>
                {{ tr('app.views.cashRegister.details', 'Detalles') }}
              </BaseButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/ui/BaseButton.vue'

interface Report {
  session_number: number
  session_id: number
  total_transactions: number
  opened_at: string
  closed_at?: string
  total_sales?: number
  total_expenses?: number
  net_cash_flow?: number
}

interface Props {
  reports: Report[]
}

defineProps<Props>()

defineEmits<{
  'view-details': [sessionId: number]
}>()

const { t, te } = useI18n()

const tr = (key: string, fallback: string) => (te(key) ? t(key) : fallback)

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch (error) {
    return 'Invalid date'
  }
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
  }).format(amount)
}
</script>
